import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { api } from "../lib/api";
import { setUserId } from '@amplitude/analytics-browser';

const AuthContext = createContext(null);

export const GetAuthToken = () => {
  const hash = window.location.hash;
  const params = new URLSearchParams(hash.split("?")[1]);
  return (
    params.get("session_id") ||
    (() => {
      try {
        return window.localStorage.getItem("session_token");
      } catch {
        return null;
      }
    })()
  );
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState([]); // array of route_ids
  const [myCreator, setMyCreator] = useState(null);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me", {
        headers: { Authorization: `Bearer ${GetAuthToken()}` },
      });
      setUserId(data.user_id);
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loadFavorites = useCallback(async () => {
    if (!user) return setFavorites([]);
    try {
      const { data } = await api.get("/me/favorites");
      setFavorites(data.route_ids || []);
    } catch {
      setFavorites([]);
    }
  }, [user]);

  useEffect(() => {
    loadFavorites();
    // load my creator when user changes
    const loadMyCreator = async () => {
      if (!user) return setMyCreator(null);
      try {
        const { data } = await api.get("/me/creator");
        // API returns `creator: null` when none exists (server may return object directly)
        if (data && data.creator === null) {
          setMyCreator(null);
        } else {
          setMyCreator(data);
        }
      } catch (e) {
        setMyCreator(null);
      }
    };
    loadMyCreator();
  }, [user, loadFavorites]);

  const toggleFavorite = async (routeId) => {
    if (!user) return false;
    if (favorites.includes(routeId)) {
      await api.delete(`/me/favorites/${routeId}`);
      setFavorites((prev) => prev.filter((id) => id !== routeId));
    } else {
      await api.post(`/me/favorites/${routeId}`);
      setFavorites((prev) => [...prev, routeId]);
    }
    return true;
  };

  const logout = async () => {
    await api.post("/auth/logout");
    // Clear any stored token fallback and Authorization header
    try {
      window.localStorage.removeItem("session_token");
    } catch {}
    if (api.defaults.headers.common["Authorization"]) {
      delete api.defaults.headers.common["Authorization"];
    }
    setUser(null);
    setFavorites([]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        myCreator,
        setUser,
        loading,
        logout,
        refresh: checkAuth,
        setMyCreator,
        favorites,
        toggleFavorite,
        loadFavorites,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
